import { Link } from "react-router-dom";
import { Star, GitCompareArrows, Check, ArrowLeft } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Database } from "@/integrations/supabase/types";
import { useCompare } from "@/contexts/CompareContext";

type System = Database['public']['Tables']['systems']['Row'] & {
  category?: { name_he: string } | null;
  system_images?: { public_url: string; is_cover?: boolean }[];
  rating_avg?: number | null;
  rating_count?: number | null;
};

interface SystemCardProps {
  system: System;
}

export const SystemCard = ({ system }: SystemCardProps) => {
  // Find the image marked as cover, otherwise fallback to the first image, otherwise placeholder
  const coverImageObj = system.system_images?.find(img => img.is_cover) || system.system_images?.[0];
  const coverImage = coverImageObj?.public_url || '/placeholder.svg';
  
  const rating = system.rating_avg || 0;
  const { add, remove, has, isFull } = useCompare();
  const isSelected = has(system.id);

  const toggleCompare = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isSelected) {
      remove(system.id);
    } else {
      add({
        id: system.id,
        title: system.title,
        slug: system.slug,
        short_desc: system.short_desc,
        cover_image: coverImage,
        category_name: system.category?.name_he || null,
        rating_avg: rating,
        rating_count: system.rating_count || 0,
      });
    }
  };

  return (
    <Link to={`/systems/${system.slug || system.id}`} className="block h-full outline-none">
      <div className="flex flex-col h-full bg-white rounded-[24px] overflow-hidden border border-slate-100 shadow-[0_4px_24px_rgba(0,0,0,0.03)] hover:shadow-[0_12px_32px_rgba(0,0,0,0.06)] transition-shadow duration-300">
        
        {/* Image Container */}
        <div className="aspect-[16/10] w-full overflow-hidden bg-slate-50 relative">
          <img 
            src={coverImage} 
            alt={system.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
          
          {system.category && (
            <Badge className="absolute top-4 end-4 bg-white/95 backdrop-blur-md text-slate-900 border-none shadow-sm font-bold text-xs px-3 py-1.5 rounded-full">
              {system.category.name_he}
            </Badge>
          )}

          <button
            onClick={toggleCompare}
            disabled={isFull && !isSelected}
            className={`absolute top-4 start-4 w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
              isSelected
                ? "bg-slate-900 text-white shadow-md"
                : isFull
                  ? "bg-white/70 text-slate-300 cursor-not-allowed"
                  : "bg-white/90 text-slate-600 hover:bg-slate-900 hover:text-white shadow-sm"
            }`}
            title={isSelected ? "הסר מהשוואה" : isFull ? "ניתן להשוות עד 3" : "הוסף להשוואה"}
          >
            {isSelected ? <Check className="w-4 h-4" /> : <GitCompareArrows className="w-4 h-4" />}
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6 flex flex-col flex-1">
          <div className="flex justify-between items-start gap-3 mb-3">
            <h3 className="font-bold text-lg text-slate-900 line-clamp-1">
              {system.title}
            </h3>
            {rating > 0 && (
              <div className="flex items-center gap-1.5 bg-amber-50/50 border border-amber-100/50 px-2 py-1 rounded-lg shrink-0">
                <span className="text-xs font-bold text-slate-800">
                  {rating.toFixed(1)}
                </span>
                <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
              </div>
            )}
          </div>
          
          <p className="text-slate-500 text-sm leading-relaxed line-clamp-2 mb-6">
            {system.short_desc}
          </p>

          <div className="mt-auto pt-4 border-t border-slate-100 flex items-center justify-between">
            <span className="text-sm font-bold text-blue-600">לפרטים נוספים</span>
            <ArrowLeft className="w-4 h-4 text-blue-600" />
          </div>
        </div>
      </div>
    </Link>
  );
};